import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { usePedidoPorEvento } from "@/hooks/usePedidoPorEvento";
import { EventoAgenda, calcularDiasAteVencimento } from "@/hooks/useAgenda";
import { CriarPedidoRenovacaoDialog } from "./CriarPedidoRenovacaoDialog";
import {
  podeIniciarRenovacao,
  mensagemRenovacaoIndisponivel,
} from "@/lib/renovacaoPrazo";
import { formatAgendaCicloLabel } from "@/lib/pedidosCiclo";

interface VerPedidoModalProps {
  evento: EventoAgenda | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <Label className="text-xs text-muted-foreground font-medium">{label}</Label>
      <div className="text-sm text-gray-900 mt-0.5 break-words">{children ?? "—"}</div>
    </div>
  );
}

function formatMoney(v?: number | null) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
}

function formatDate(v?: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("pt-BR");
}

function statusBadgeClass(status?: string | null) {
  const s = (status || "").toLowerCase();
  if (s === "aprovado" || s === "concluido" || s === "ativo" || s === "finalizado") {
    return "bg-gray-800 text-white hover:bg-gray-800";
  }
  if (s === "cancelado" || s === "inativo" || s === "rejeitado") {
    return "bg-gray-200 text-gray-700 hover:bg-gray-200";
  }
  return "bg-gray-100 text-gray-800 hover:bg-gray-100";
}

export function VerPedidoModal({ evento, open, onOpenChange }: VerPedidoModalProps) {
  const { data: pedido, isLoading, error } = usePedidoPorEvento(evento);
  const [showCriarRenovacao, setShowCriarRenovacao] = useState(false);

  const dataFimRenovacao = evento?.data_fim ?? pedido?.contrato?.data_fim ?? null;
  const diasAteRenovacao = calcularDiasAteVencimento(dataFimRenovacao);
  const naJanelaRenovacao = podeIniciarRenovacao(dataFimRenovacao);
  const msgRenovacaoBloqueada = mensagemRenovacaoIndisponivel(diasAteRenovacao);

  const podeCriarRenovacao =
    evento?.tipo === "renovacao_seguro" &&
    pedido?.id &&
    pedido.situacao_renovacao !== "renovado" &&
    pedido.situacao_renovacao !== "nao_renovado" &&
    naJanelaRenovacao;

  if (error) {
    console.error("Erro no modal VerPedidoModal:", error);
  }

  if (!evento) return null;

  const tipoLabel =
    evento.tipo === "renovacao_seguro"
      ? formatAgendaCicloLabel(evento.numero_ciclo)
      : evento.tipo.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const pedidoNumero = pedido?.id != null ? `#${String(pedido.id).padStart(6, "0")}` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden gap-3">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span>{evento.titulo}</span>
            <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">{tipoLabel}</Badge>
            {pedido?.status && (
              <Badge className={statusBadgeClass(pedido.status)}>
                {pedido.status.charAt(0).toUpperCase() + pedido.status.slice(1).replace(/_/g, " ")}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-5 pr-1">
          {isLoading && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Carregando informações...
            </div>
          )}

          {error && (
            <div className="py-8 text-center text-sm text-red-600">
              Erro ao carregar o pedido. Tente novamente.
            </div>
          )}

          {!isLoading && (
            <>
              <section className="space-y-3">
                <p className="text-sm font-medium text-gray-900">Evento</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <Field label="Título">{evento.titulo}</Field>
                  <Field label="Tipo">
                    <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">{tipoLabel}</Badge>
                  </Field>
                  <Field label="Prioridade">
                    <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                      {evento.prioridade.charAt(0).toUpperCase() + evento.prioridade.slice(1)}
                    </Badge>
                  </Field>
                  <Field label="Data início">{formatDate(evento.data_inicio)}</Field>
                  <Field label="Data fim">{formatDate(evento.data_fim)}</Field>
                  <Field label="Dias até vencimento">
                    {diasAteRenovacao == null
                      ? "—"
                      : diasAteRenovacao < 0
                        ? `Vencido há ${Math.abs(diasAteRenovacao)} dias`
                        : diasAteRenovacao === 0
                          ? "Vence hoje"
                          : `${diasAteRenovacao} dias`}
                  </Field>
                  {evento.descricao && (
                    <div className="sm:col-span-2 md:col-span-3">
                      <Field label="Descrição">{evento.descricao}</Field>
                    </div>
                  )}
                </div>
              </section>

              {pedido && (
                <>
                  <section className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium text-gray-900">Pedido {pedidoNumero}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <Field label="Número">{pedidoNumero}</Field>
                      <Field label="Status">
                        <Badge className={statusBadgeClass(pedido.status)}>
                          {pedido.status
                            ? pedido.status.charAt(0).toUpperCase() + pedido.status.slice(1)
                            : "—"}
                        </Badge>
                      </Field>
                      <Field label="Valor total">{formatMoney(pedido.valor_total)}</Field>
                      <Field label="Prazo de entrega">
                        {pedido.prazo_entrega != null ? `${pedido.prazo_entrega} dias` : "—"}
                      </Field>
                      <Field label="Criado em">{formatDate(pedido.created_at)}</Field>
                      <Field label="Atualizado em">{formatDate(pedido.updated_at)}</Field>
                    </div>
                  </section>

                  {pedido.cliente && (
                    <section className="space-y-3 border-t pt-4">
                      <p className="text-sm font-medium text-gray-900">Cliente</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <Field label="Nome">{pedido.cliente.nome || "—"}</Field>
                        <Field label="CPF/CNPJ">{pedido.cliente.cpf_cnpj || "—"}</Field>
                        <Field label="E-mail">{pedido.cliente.email || "—"}</Field>
                        <Field label="Telefone">{pedido.cliente.telefone || "—"}</Field>
                      </div>
                    </section>
                  )}

                  {pedido.orcamento && (
                    <section className="space-y-3 border-t pt-4">
                      <p className="text-sm font-medium text-gray-900">Orçamento</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <Field label="ID">#{pedido.orcamento.id}</Field>
                        <Field label="Status">
                          {pedido.orcamento.status ? (
                            <Badge className={statusBadgeClass(pedido.orcamento.status)}>
                              {pedido.orcamento.status.charAt(0).toUpperCase() +
                                pedido.orcamento.status.slice(1)}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </Field>
                        <Field label="Valor total">{formatMoney(pedido.orcamento.valor_total)}</Field>
                        <Field label="Valor do seguro">{formatMoney(pedido.orcamento.valor_seguro)}</Field>
                        <Field label="Painéis">{pedido.orcamento.quantidade_placas ?? "—"}</Field>
                        <Field label="Vigência">
                          {pedido.orcamento.vigencia_anos
                            ? `${pedido.orcamento.vigencia_anos} ano(s)`
                            : "—"}
                        </Field>
                        {pedido.orcamento.data_aprovacao && (
                          <Field label="Aprovado em">{formatDate(pedido.orcamento.data_aprovacao)}</Field>
                        )}
                        {pedido.orcamento.aprovado_por && (
                          <Field label="Aprovado por">{pedido.orcamento.aprovado_por}</Field>
                        )}
                      </div>
                    </section>
                  )}

                  {pedido.projeto && (
                    <section className="space-y-3 border-t pt-4">
                      <p className="text-sm font-medium text-gray-900">Projeto</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <Field label="ID interno">{pedido.projeto.id}</Field>
                        <Field label="Status">{pedido.projeto.status || "—"}</Field>
                        <Field label="Valor">{formatMoney(pedido.projeto.valor_projeto)}</Field>
                        <Field label="Departamento">{pedido.projeto.departamento || "—"}</Field>
                        {(pedido.projeto as any).empresa_responsavel && (
                          <Field label="Empresa responsável">
                            {(pedido.projeto as any).empresa_responsavel}
                          </Field>
                        )}
                        {pedido.projeto.observacoes && (
                          <div className="sm:col-span-2 md:col-span-3">
                            <Field label="Observações">{pedido.projeto.observacoes}</Field>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {pedido.contrato && (
                    <section className="space-y-3 border-t pt-4">
                      <p className="text-sm font-medium text-gray-900">Contrato</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <Field label="Número">{pedido.contrato.numero_contrato || "—"}</Field>
                        <Field label="Status">
                          {pedido.contrato.status ? (
                            <Badge className={statusBadgeClass(pedido.contrato.status)}>
                              {pedido.contrato.status.charAt(0).toUpperCase() +
                                pedido.contrato.status.slice(1)}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </Field>
                        <Field label="Valor">{formatMoney(pedido.contrato.valor)}</Field>
                        <Field label="Início">{formatDate(pedido.contrato.data_inicio)}</Field>
                        <Field label="Fim">{formatDate(pedido.contrato.data_fim)}</Field>
                      </div>
                    </section>
                  )}
                </>
              )}

              {!isLoading && !error && !pedido && (
                <p className="text-sm text-muted-foreground py-4">
                  Nenhum pedido vinculado a este evento.
                </p>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 flex flex-col items-end gap-2 border-t pt-3">
          {evento.tipo === "renovacao_seguro" &&
            pedido?.id &&
            pedido.situacao_renovacao !== "renovado" &&
            pedido.situacao_renovacao !== "nao_renovado" &&
            !naJanelaRenovacao && (
              <p className="text-sm text-gray-600 text-right max-w-md">{msgRenovacaoBloqueada}</p>
            )}
          <div className="flex justify-end gap-2">
            {podeCriarRenovacao && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => setShowCriarRenovacao(true)}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Criar proposta de renovação
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>

      <CriarPedidoRenovacaoDialog
        open={showCriarRenovacao}
        onOpenChange={setShowCriarRenovacao}
        pedidoAnteriorId={pedido?.id ?? null}
        evento={evento}
        onSuccess={() => onOpenChange(false)}
      />
    </Dialog>
  );
}
